import { Store } from '../store'; // eslint-disable-line no-unused-vars
import { makeAutoObservable, runInAction } from 'mobx';
import { API_ENDPOINTS } from '../../api/endpoints';
import { mapData, mapCompanyEntry } from '../../api/dataMappers';
import {
  PROFILER_INDUSTRY_CODES_SEARCH_FIELDS,
  PROFILER_PROJECT_FIELDS,
  PROFILER_SEARCH_FIELDS
} from '../../utils/constants/fields';
import { ACCOUNT_VERIF_TYPES } from '../../utils/constants/auth';
import { HEADER_MESSAGE_TYPES } from '../../utils/constants/header';
import pubSub from '../../utils/pubsub';
import { WSS_EVENTS, WSS_ACTIONS } from './WebSocketStore';
import { UI_OPTIONS } from '../../utils/constants/uiOptions';
import { UI_OPTION_KEYS } from '../../utils/constants/uiOptionKeys';
import {
  eventWatchlistItemAdded,
  eventWatchlistItemRemoved,
  eventProjectCreated,
  eventProjectArchived,
  eventProjectRestored,
  eventProjectDeleted,
  eventTeamRequestShared,
  eventTeamFeedbackSubmitted,
  eventTeamRequestResolved
} from '../../ga4/ga4';

/**
 * Which criteria-field set a project's `searchFields` should be mapped through,
 * or null when it has none.
 *
 * Bulk search returns null so the callers skip `mapData` entirely — mapData(null, …)
 * throws on the empty bulk searchFields shape — and `sourceFileName` rides through
 * on the spread instead. It is compared against the literal token rather than
 * UI_OPTIONS[…].Bulk because that picker option is env-gated (non-prod only) while
 * recognising an existing bulk project has to work in every environment.
 *
 * Everything else, including Characteristics and an unknown category, maps through
 * the default criteria fields.
 */
const resolveFieldsToMap = (searchCategory) => {
  if (searchCategory === UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Industries']) {
    return PROFILER_INDUSTRY_CODES_SEARCH_FIELDS;
  }
  if (searchCategory === 'companyBulkSearch') {
    return null;
  }
  return PROFILER_SEARCH_FIELDS;
};

class ProfilerProjectsStore {
  /**
   * @param {Store} root
   */
  constructor(root) {
    makeAutoObservable(this);
    this.root = root;
  }

  reset = () => {
    this.wssQuery = [];
    this.allProjects = [];
    this.loadProjectsError = null;
    this.isLoadingProjects = false;
    this.initialLoadProjects = true;
    this.isRequestingProjectFeedback = false;
    this.isResolvingProjectFeedback = false;
    this.isUpdatingProject = false;
    this.isArchivingProject = false;
    this.isDeletingProjects = false;
    this.watchlist = [];
    this.loadWatchlistError = null;
    this.isLoadingWatchlist = false;
    this.initialLoadWatchlist = true;
    this.companiesInWatchlistAction = {};
    this.removingFromWatchlistEntries = {};
    this.addToWatchlistQuery = [];
  };

  wssQuery = [];
  allProjects = [];
  loadProjectsError = null;
  isLoadingProjects = false;
  initialLoadProjects = true;
  isRequestingProjectFeedback = false;
  isResolvingProjectFeedback = false;
  isUpdatingProject = false;
  isArchivingProject = false;
  isDeletingProjects = false;
  watchlist = [];
  loadWatchlistError = null;
  isLoadingWatchlist = false;
  initialLoadWatchlist = true;
  companiesInWatchlistAction = {};
  removingFromWatchlistEntries = {};
  addToWatchlistQuery = []; // remove when API_ENDPOINTS.ADD_TO_PROFILER_WATCHLIST is fixed

  _wss_onData = (jsData = {}) => {
    if (
      [
        WSS_EVENTS.PROFILER_PROJECTS_STORE.PROFILER_PROJECT,
        WSS_EVENTS.PROFILER_PROJECTS_STORE.ROFILER_PROJECT_OWNER_LAST_CHECKED_DATE_UPDATE
      ].includes(jsData.for) &&
      (this.isLoadingProjects || this.initialLoadProjects || this.loadProjectsError)
    ) {
      return;
    }

    if (
      [
        WSS_EVENTS.PROFILER_PROJECTS_STORE.PROFILER_WATCHLIST_COMPANY,
        WSS_EVENTS.PROFILER_PROJECTS_STORE.PROFILER_WATCHLIST_NEWS_UPDATE
      ].includes(jsData.for) &&
      (this.isLoadingWatchlist || this.initialLoadWatchlist || this.loadWatchlistError)
    ) {
      return;
    }

    if (jsData.for === WSS_EVENTS.PROFILER_PROJECTS_STORE.PROFILER_PROJECT) {
      if ([WSS_ACTIONS.CREATE, WSS_ACTIONS.UPDATE].includes(jsData?.action)) {
        jsData.data.searchCategory =
          jsData.data.searchCategory ||
          UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Characteristics'];
        const fieldsToMap = resolveFieldsToMap(jsData.data.searchCategory);

        jsData.data = {
          ...jsData.data,
          ...mapData(jsData.data, PROFILER_PROJECT_FIELDS),
          ...(jsData.data.searchFields && fieldsToMap
            ? { fields: mapData(jsData.data.searchFields, fieldsToMap) }
            : {})
        };
      }
      runInAction(() => {
        if (jsData?.action === WSS_ACTIONS.CREATE) {
          this._wss_onCreateProject(jsData);
        } else if (jsData?.action === WSS_ACTIONS.UPDATE) {
          this._wss_onUpdateProject(jsData);
        } else if (jsData?.action === WSS_ACTIONS.DELETE) {
          this._wss_onDeleteProject(jsData);
        }
      });
    } else if (
      jsData.for ===
      WSS_EVENTS.PROFILER_PROJECTS_STORE.ROFILER_PROJECT_OWNER_LAST_CHECKED_DATE_UPDATE
    ) {
      runInAction(() => {
        this._wss_onOwnerLastCheckedDateUpdate(jsData);
      });
    } else if (jsData.for === WSS_EVENTS.PROFILER_PROJECTS_STORE.PROFILER_WATCHLIST_COMPANY) {
      runInAction(() => {
        if (jsData?.action === WSS_ACTIONS.CREATE) {
          this._wss_onWatchlistCompanyCreate(jsData.data);
        } else if (jsData?.action === WSS_ACTIONS.DELETE) {
          this._wss_onWatchlistCompanyDelete(jsData.data);
        }
      });
    } else if (jsData.for === WSS_EVENTS.PROFILER_PROJECTS_STORE.PROFILER_WATCHLIST_NEWS_UPDATE) {
      runInAction(() => {
        this._wss_onWatchlistCompanyNewsSeenDateUpdate(jsData);
      });
    }
  };

  _wss_onCreateProject = (jsData) => {
    const project = jsData.data;

    const foundProject = this.allProjects.find((p) => p.id === project.id);
    if (foundProject) {
      Object.entries(project).forEach(([prop, value]) => {
        foundProject[prop] = value;
      });
    } else {
      this.allProjects.push(project);
    }
  };

  _wss_onUpdateProject = (jsData) => {
    const project = jsData.data;

    const foundProject = this.allProjects.find((p) => p.id === project.id);
    if (foundProject) {
      let shouldUpdateLastCheckedDate = false;
      if (foundProject.ownerId === this.root.authStore.currentUserId) {
        const foundProjectLastResolvedDate =
          foundProject?.sharedUsers?.sort?.((a, b) => b.dateResolved - a.dateResolved)?.[0]
            ?.dateResolved || 0;
        const updatedProjectDataLastResolvedDate =
          project?.sharedUsers?.sort?.((a, b) => b.dateResolved - a.dateResolved)?.[0]
            ?.dateResolved || 0;
        if (
          updatedProjectDataLastResolvedDate > foundProjectLastResolvedDate &&
          updatedProjectDataLastResolvedDate > foundProject.ownerLastCheckedDate
        ) {
          shouldUpdateLastCheckedDate = true;
        }
      }
      Object.entries(project).forEach(([prop, value]) => {
        foundProject[prop] = value;
      });
      if (shouldUpdateLastCheckedDate) {
        setTimeout(() => {
          pubSub.publish({
            event: 'update-profiler-project-last-checked-date',
            projectId: project.id
          });
        }, 0);
      }
    }
  };

  _wss_onDeleteProject = (jsData) => {
    const projectId = jsData.data?.id;

    const foundProjectIndex = this.allProjects.findIndex((p) => p.id === projectId);
    if (foundProjectIndex !== -1) {
      this.allProjects.splice(foundProjectIndex, 1);
    }
  };

  _wss_onOwnerLastCheckedDateUpdate = (jsData) => {
    const projectId = jsData.data?.projectId;

    const foundProjectIndex = this.allProjects.findIndex((p) => p.id === projectId);
    if (foundProjectIndex !== -1) {
      this.allProjects[foundProjectIndex].ownerLastCheckedDate = jsData.data.date;
    }
  };

  _wss_onWatchlistCompanyCreate = (jsData) => {
    const foundCompanyIndex = this.watchlist.findIndex((c) => c.id === jsData.data?.id);
    if (foundCompanyIndex !== -1) {
      return;
    }

    this.watchlist.unshift(jsData.data);
  };

  _wss_onWatchlistCompanyDelete = (jsData) => {
    const companyId = jsData.data?.companyId;
    const foundCompanyIndex = this.watchlist.findIndex((c) => c.id === companyId);
    if (foundCompanyIndex === -1) {
      return;
    }

    // pubSub.publish({ event: 'remove-watchlist-company', companyId });
    this.watchlist.splice(foundCompanyIndex, 1);
  };

  _wss_onWatchlistCompanyNewsSeenDateUpdate = (jsData) => {
    const companyId = jsData.data?.companyId;

    const foundCompanyIndex = this.watchlist.findIndex((c) => c.id === companyId);
    if (foundCompanyIndex !== -1) {
      this.watchlist[foundCompanyIndex].lastSeenNews = jsData.data.timestamp;
    }
  };

  // active
  get projects() {
    const currentUserId = this.root.authStore.currentUserId;

    if (!currentUserId) {
      return [];
    }

    return this.allProjects.filter((p) => p.ownerId === currentUserId && p.status === 'ACTIVE');
  }

  // archived
  get archivedProjects() {
    const currentUserId = this.root.authStore.currentUserId;

    if (!currentUserId) {
      return [];
    }

    return this.allProjects.filter((p) => p.ownerId === currentUserId && p.status === 'ARCHIVED');
  }

  get pendingSharedProjects() {
    const currentUserId = this.root.authStore.currentUserId;

    if (!currentUserId) {
      return [];
    }

    return this.allProjects.filter((p) => {
      const foundUser = p.sharedUsers?.find?.((user) => user.id === currentUserId);
      if (!foundUser) {
        return false;
      }
      return foundUser.status === 'PENDING';
    });
  }

  get resolvedSharedProjects() {
    const currentUserId = this.root.authStore.currentUserId;

    if (!currentUserId) {
      return [];
    }

    return this.allProjects.filter((p) => {
      const foundUser = p.sharedUsers?.find?.((user) => user.id === currentUserId);
      if (!foundUser) {
        return false;
      }
      return foundUser.status === 'RESOLVED';
    });
  }

  get hasNewProjectFeedback() {
    const currentUserId = this.root.authStore.currentUserId;

    return !!this.allProjects.filter((p) => {
      if (p.ownerId !== currentUserId || p.status === 'ARCHIVED') {
        return false;
      }

      return this.hasProjectNewFeedback(p);
    }).length;
  }

  hasProjectNewFeedback = (project) => {
    const ownerLastCheckedDate = project.ownerLastCheckedDate || 0;
    if (
      project.sharedUsers?.findIndex?.(
        (u) => !!u.dateResolved && u.dateResolved > ownerLastCheckedDate
      ) !== -1
    ) {
      return true;
    }

    return false;
  };

  get isLoadingProjectsData() {
    return this.isLoadingProjects || this.initialLoadProjects;
  }

  loadProjects = async () => {
    if (
      this.isLoadingProjects ||
      this.root.authStore.userVerificationStatus !== ACCOUNT_VERIF_TYPES.APPROVED ||
      !this.root.profilerAccessStore.hasAnyProfilerAccess
    ) {
      if (!this.root.profilerAccessStore.hasAnyProfilerAccess && this.initialLoadProjects) {
        runInAction(() => {
          this.initialLoadProjects = false;
        });
      }
      return;
    }

    this.isLoadingProjects = true;
    this.loadProjectsError = null;

    // Owner names come from the same company-users list AuthStore already
    // fetches on connect, so await its shared request rather than putting the
    // endpoint in a batch here: AuthStore's in-flight guard cannot see this
    // call, so the two together fetched it twice on every socket connect.
    //
    // A null result means the request failed, which has to stop the load. The
    // owner filter below keeps a project only when its owner appears in this
    // list, so mapping against an empty one silently discards EVERY project,
    // including the user's own — the batch this replaced surfaced the failure
    // instead. The try/catch is what stops a rejection here stranding
    // isLoadingProjects true, which the entry guard would then read as "already
    // loading" and never retry.
    let companyUsers = null;
    try {
      companyUsers = await this.root.authStore.loadCompanyUsers();
    } catch {
      companyUsers = null;
    }

    if (!companyUsers) {
      runInAction(() => {
        this.loadProjectsError = '[Profiler] Failed to obtain profiler projects.';
        this.isLoadingProjects = false;
        if (this.initialLoadProjects) {
          this.initialLoadProjects = false;
        }
      });
      return;
    }

    void this.root.makeRequest({
      endpoint: API_ENDPOINTS.GET_PROFILER_PROJECTS,
      onSuccess: (projectsResponse) => {
        let projects = projectsResponse.map((p) => {
          const searchCategory =
            p.searchCategory ||
            UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Characteristics'];
          const fieldsToMap = resolveFieldsToMap(searchCategory);

          return {
            ...p,
            ...mapData(p, PROFILER_PROJECT_FIELDS),
            searchCategory,
            ...(p.searchFields && fieldsToMap ? { fields: mapData(p.searchFields, fieldsToMap) } : {})
          };
        });

        if (!this.root.profilerAccessStore.canUseProfilerSearch) {
          // the user can only give feedback and previously had profiler search/project access
          // exclude his own projects
          projects = projects.filter((p) => p.ownerId !== this.root.authStore.currentUserId);
        }

        // if the project was shared to the current user from someone that no longer has profiler access -> exlude it
        projects = projects.filter(
          (p) =>
            companyUsers.findIndex(
              (u) =>
                (u.id !== this.root.authStore.currentUserId &&
                  u.id === p.ownerId &&
                  (u.isCognitoProfilerLite ||
                    u.isCognitoProfilerDemoLite ||
                    u.isCognitoProfilerDemoFull ||
                    u.isCognitoProfilerFull)) ||
                p.ownerId === this.root.authStore.currentUserId
            ) !== -1
        );

        this.allProjects = projects;
      },
      onError: (errorMessage) => {
        this.loadProjectsError =
          '[Profiler] ' + (errorMessage || 'Failed to obtain profiler projects.');
      },
      onFinally: () => {
        this.isLoadingProjects = false;
        if (this.initialLoadProjects) {
          this.initialLoadProjects = false;
        }
      }
    });
  };

  saveProject = (
    searchId = '',
    name = '',
    searchReason,
    onSuccess = () => {},
    onError = () => {},
    onFinally = () => {}
  ) => {
    void this.root.makeRequest({
      endpoint: API_ENDPOINTS.SAVE_PROFILER_PROJECT,
      body: { searchId, name, searchReason },
      onSuccess: (response) => {
        this.root.utilsStore.setHeaderMessage(
          `You have successfully saved project with name '${name}'.`
        );
        eventProjectCreated(response?.id || searchId, searchReason);
        onSuccess(response);
      },
      onError: (errorMessage) => {
        this.root.utilsStore.setHeaderMessage(
          `Failed to save project with name '${name}'.`,
          HEADER_MESSAGE_TYPES.ERROR
        );
        onError(errorMessage);
      },
      onFinally
    });
  };

  archiveProject = (
    projectId = '',
    archive = true,
    onSuccess = () => {},
    onError = () => {},
    onFinally = () => {}
  ) => {
    if (this.isArchivingProject) {
      return;
    }

    this.isArchivingProject = true;

    void this.root.makeRequest({
      endpoint: API_ENDPOINTS.ARCHIVE_PROFILER_PROJECT,
      body: { projectId, archive },
      onSuccess: (response) => {
        this.root.utilsStore.setHeaderMessage(
          `Successfully ${archive ? 'archived' : 'unarchived'} project.`
        );
        if (archive) eventProjectArchived(projectId);
        else eventProjectRestored(projectId);
        onSuccess(response);
      },
      onError: (errorMessage) => {
        this.root.utilsStore.setHeaderMessage(
          `Failed to ${archive ? 'archive' : 'unarchive'} project.`,
          HEADER_MESSAGE_TYPES.ERROR
        );
        onError(errorMessage);
      },
      onFinally: () => {
        this.isArchivingProject = false;
        onFinally();
      }
    });
  };

  deleteProjects = (
    projectIds = [],
    onSuccess = () => {},
    onError = () => {},
    onFinally = () => {}
  ) => {
    if (this.isDeletingProjects) {
      return;
    }

    this.isDeletingProjects = true;

    void this.root.makeRequest({
      endpoint: API_ENDPOINTS.DELETE_PROFILER_PROJECTS,
      body: { projectIds },
      onSuccess: () => {
        this.root.utilsStore.setHeaderMessage(
          `Successfully deleted ${projectIds.length ? 'projects' : 'project'}.`
        );
        (projectIds || []).forEach((id) => eventProjectDeleted(id));
        onSuccess();
      },
      onError: (errorMessage) => {
        this.root.utilsStore.setHeaderMessage(
          `Failed to delete ${projectIds.length ? 'projects' : 'project'}.`,
          HEADER_MESSAGE_TYPES.ERROR
        );
        onError(errorMessage);
      },
      onFinally: () => {
        this.isDeletingProjects = false;
        onFinally();
      }
    });
  };

  requestProjectFeedback = (
    projectId = '',
    askUserIds = [],
    note = '',
    onSuccess = () => {},
    onError = () => {},
    onFinally = () => {}
  ) => {
    if (this.isRequestingProjectFeedback) {
      return;
    }

    this.isRequestingProjectFeedback = true;

    void this.root.makeRequest({
      endpoint: API_ENDPOINTS.REQUEST_PROFILER_PROJECT_FEEDBACK,
      body: { projectId, askUserIds, note },
      onSuccess: (response) => {
        this.root.utilsStore.setHeaderMessage('Successfully requested feedback.');
        eventTeamRequestShared(projectId, (askUserIds || []).length);
        onSuccess(response);
      },
      onError: (errorMessage) => {
        // Show the backend's reason (e.g. a recipient that cannot be found, so nothing
        // was sent); fall back to the generic text when there is none.
        this.root.utilsStore.setHeaderMessage(
          errorMessage || 'Failed to request feedback.',
          HEADER_MESSAGE_TYPES.ERROR,
          8_000
        );
        onError(errorMessage);
      },
      onFinally: () => {
        this.isRequestingProjectFeedback = false;
        onFinally();
      }
    });
  };

  resolveProjectFeedback = (
    projectId = '',
    comment = '',
    feedbacks = [],
    onSuccess = () => {},
    onError = () => {},
    onFinally = () => {}
  ) => {
    if (this.isResolvingProjectFeedback) {
      return;
    }

    this.isResolvingProjectFeedback = true;

    void this.root.makeRequest({
      endpoint: API_ENDPOINTS.RESOLVE_PROFILER_PROJECT_FEEDBACK,
      body: { projectId, comment, feedbacks },
      onSuccess: (response) => {
        this.root.utilsStore.setHeaderMessage('Successfully resolved feedback.');
        if (comment) eventTeamFeedbackSubmitted(projectId, comment.length);
        eventTeamRequestResolved(projectId);
        onSuccess(response);
      },
      onError: (errorMessage) => {
        this.root.utilsStore.setHeaderMessage(
          'Failed to resolve feedback.',
          HEADER_MESSAGE_TYPES.ERROR
        );
        onError(errorMessage);
      },
      onFinally: () => {
        this.isResolvingProjectFeedback = false;
        onFinally();
      }
    });
  };

  updateProject = (
    projectId = '',
    name = '',
    searchReason = null,
    onSuccess = () => {},
    onError = () => {},
    onFinally = () => {}
  ) => {
    if (this.isUpdatingProject) {
      return;
    }

    this.isUpdatingProject = true;

    void this.root.makeRequest({
      endpoint: API_ENDPOINTS.UPDATE_PROFILER_PROJECT,
      body: { projectId, name, searchReason },
      onSuccess: (response) => {
        this.root.utilsStore.setHeaderMessage(`Successfully updated project '${name}'.`);
        onSuccess(response);
      },
      onError: (errorMessage) => {
        this.root.utilsStore.setHeaderMessage(
          errorMessage || `Failed to update project '${name}'.`,
          HEADER_MESSAGE_TYPES.ERROR
        );
        onError(errorMessage || `Failed to update project '${name}'.`);
      },
      onFinally: () => {
        this.isUpdatingProject = false;
        onFinally();
      }
    });
  };

  updateProjectLastCheckedDate = (
    projectId = '',
    onSuccess = () => {},
    onError = () => {},
    onFinally = () => {}
  ) => {
    const foundProject = this.allProjects.find((p) => p.id === projectId);
    if (!foundProject) {
      return;
    }

    const ownerLastCheckedDate = foundProject.ownerLastCheckedDate || 0;
    const lastResolvedDate =
      foundProject.sharedUsers
        ?.filter?.((u) => !!u.dateResolved)
        ?.sort?.((a, b) => b.dateResolved - a.dateResolved)?.[0]?.dateResolved || 0;

    if (!lastResolvedDate || ownerLastCheckedDate > lastResolvedDate) {
      return;
    }

    void this.root.makeRequest({
      endpoint: API_ENDPOINTS.UPDATE_PROFILER_PROJECT_LAST_CHECKED_DATE,
      body: { projectId, date: lastResolvedDate + 1 },
      onSuccess: (response) => {
        onSuccess(response);
      },
      onError: (errorMessage) => {
        onError(errorMessage);
      },
      onFinally: () => {
        onFinally();
      }
    });
  };

  loadWatchlist = () => {
    if (
      this.isLoadingWatchlist ||
      this.root.authStore.userVerificationStatus !== ACCOUNT_VERIF_TYPES.APPROVED ||
      !this.root.profilerAccessStore.canViewProfilerWatchlist
    ) {
      return;
    }

    this.isLoadingWatchlist = true;
    this.loadWatchlistError = null;
    void this.root.makeRequest({
      endpoint: API_ENDPOINTS.GET_PROFILER_WATCHLIST,
      onSuccess: (response) => {
        this.watchlist = response;
      },
      onError: (errorMessage) => {
        this.loadWatchlistError = errorMessage || 'Failed to obtain watchlist.';
      },
      onFinally: () => {
        this.isLoadingWatchlist = false;
        if (this.initialLoadWatchlist) {
          this.initialLoadWatchlist = false;
        }
      }
    });
  };

  // addToWatchlist = (
  //   company = {},
  //   onSuccess = () => {},
  //   onError = () => {},
  //   onFinally = () => {}
  // ) => {
  //   const [companyId, companyName] = [company.id, company.name];

  //   if (this.companiesInWatchlistAction[companyId]) {
  //     return;
  //   }

  //   const add = this.watchlist.findIndex((c) => c.id === companyId) === -1;
  //   runInAction(() => {
  //     this.companiesInWatchlistAction[companyId] = true;
  //     if (!add) {
  //       this.removingFromWatchlistEntries[companyId] = true;
  //     }
  //   });

  //   this.root.makeRequest({
  //     endpoint: API_ENDPOINTS.ADD_TO_PROFILER_WATCHLIST,
  //     body: { companyId, add },
  //     onSuccess: (response) => {
  //       if (add) {
  //         this._wss_onWatchlistCompanyCreate({ data: mapCompanyEntry(response) });
  //         this.root.utilsStore.setHeaderMessage(
  //           `You have successfully added company '${companyName}' to your watchlist.`
  //         );
  //       } else {
  //         this._wss_onWatchlistCompanyDelete({ data: { companyId } });
  //         this.root.utilsStore.setHeaderMessage(
  //           `You have successfully removed company '${companyName}' from your watchlist.`
  //         );
  //       }

  //       onSuccess(response);
  //     },
  //     onError: (errorMessage) => {
  //       this.root.utilsStore.setHeaderMessage(
  //         `Failed to ${add ? 'add' : 'remove'} company '${companyName}' ${
  //           add ? 'to' : 'from'
  //         } your watchlist.`,
  //         HEADER_MESSAGE_TYPES.ERROR
  //       );
  //       onError(errorMessage);
  //     },
  //     onFinally: () => {
  //       this.companiesInWatchlistAction[companyId] = false;
  //       this.removingFromWatchlistEntries[companyId] = false;
  //       onFinally();
  //     }
  //   });
  // };

  // remove when API_ENDPOINTS.ADD_TO_PROFILER_WATCHLIST is fixed [ START ]
  addToWatchlist = (
    company = {},
    onSuccess = () => {},
    onError = () => {},
    onFinally = () => {}
  ) => {
    const [companyId, companyName] = [company.id, company.name];

    if (this.companiesInWatchlistAction[companyId]) {
      return;
    }

    const add = this.watchlist.findIndex((c) => c.id === companyId) === -1;

    // Best-effort source attribution (non-PII) from the page the action originated on.
    const watchlistSource = (() => {
      const path = typeof window !== 'undefined' ? window.location.pathname : '';
      if (path.includes('company-details')) return 'company_profile';
      if (path.includes('search-results')) return 'search_results';
      if (path.includes('agent')) return 'agentic_ai';
      if (path.includes('watch-list')) return 'watchlist';
      return 'other';
    })();

    runInAction(() => {
      this.companiesInWatchlistAction[companyId] = true;
      if (!add) {
        this.removingFromWatchlistEntries[companyId] = true;
      }
    });

    const task = () => {
      return this.root.makeRequest({
        endpoint: API_ENDPOINTS.ADD_TO_PROFILER_WATCHLIST,
        body: { companyId, add },
        onSuccess: (response) => {
          if (add) {
            this._wss_onWatchlistCompanyCreate({ data: mapCompanyEntry(response) });
            this.root.utilsStore.setHeaderMessage(
              `You have successfully added company '${companyName}' to your watchlist.`
            );
            eventWatchlistItemAdded(companyId, watchlistSource);
          } else {
            this._wss_onWatchlistCompanyDelete({ data: { companyId } });
            this.root.utilsStore.setHeaderMessage(
              `You have successfully removed company '${companyName}' from your watchlist.`
            );
            eventWatchlistItemRemoved(companyId);
          }

          onSuccess(response);
        },
        onError: (errorMessage) => {
          this.root.utilsStore.setHeaderMessage(
            `Failed to ${add ? 'add' : 'remove'} company '${companyName}' ${
              add ? 'to' : 'from'
            } your watchlist.`,
            HEADER_MESSAGE_TYPES.ERROR
          );
          onError(errorMessage);
        },
        onFinally: () => {
          this.companiesInWatchlistAction[companyId] = false;
          this.removingFromWatchlistEntries[companyId] = false;
          onFinally();
          this._dequeueWatchlistTask();
        }
      });
    };

    this.addToWatchlistQuery.push(task);
    if (this.addToWatchlistQuery.length === 1) {
      void task();
    }
  };

  _dequeueWatchlistTask = () => {
    this.addToWatchlistQuery.shift();
    if (this.addToWatchlistQuery.length > 0) {
      this.addToWatchlistQuery[0]();
    }
  };
  // remove when API_ENDPOINTS.ADD_TO_PROFILER_WATCHLIST is fixed [  END  ]

  updateWatchlistCompanyNewsSeenDate = (
    companyId = '',
    onSuccess = () => {},
    onError = () => {},
    onFinally = () => {}
  ) => {
    // Existence check only — prefer `.some` over unused `.find` result (S7754).
    if (!this.watchlist.some((c) => c.id === companyId)) {
      return;
    }

    void this.root.makeRequest({
      endpoint: API_ENDPOINTS.UPDATE_PROFILER_WATCHLIST_NEWS_CHECKED,
      body: { companyId, timestamp: Date.now() },
      onSuccess: (response) => {
        onSuccess(response);
      },
      onError: (errorMessage) => {
        onError(errorMessage);
      },
      onFinally: () => {
        onFinally();
      }
    });
  };
}

export default ProfilerProjectsStore;
